import React, { useEffect, useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { Button, Col, Row, FormGroup, Label } from 'reactstrap';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import Switch from "react-switch";
import { useDispatch } from 'react-redux';
import { addCustomerRequest, updateCustomerRequest } from 'store/actions';
import axiosInstance from 'pages/Utility/axiosInstance';
import Select from 'react-select';
import Breadcrumb from 'components/Common/Breadcrumb2';

const Offsymbol = () => <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", fontSize: 12, color: "#fff" }}></div>;
const OnSymbol = () => <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", fontSize: 12, color: "#fff" }}></div>;

const CreateCustomer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const previousLocation = location.state?.from || '/';
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');

  const [isActive, setIsActive] = useState(true);
  const [selectedNationality, setSelectedNationality] = useState();
  const [selectedArea, setSelectedArea] = useState();
  const [initialData, setInitialData] = useState(null);
  const [nationalities, setNationalities] = useState([]);
  const [areas, setAreas] = useState([]);

  const permissions = JSON.parse(localStorage?.getItem('permissions'));
  if ((!permissions?.map(item => item?.permissionName)?.includes("Add Customers") &&
    !permissions?.map(item => item?.permissionName)?.includes("Edit Customers"))) {
    navigate('/pages-403');
  }

  // Fetch all nationalities
  const fetchNationalities = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL_TWO}usp_GetNationality`);
      setNationalities(response?.data?.Data || []);
    } catch (error) {
      console.error('Error fetching nationality options:', error);
    }
  };

  // Fetch all areas (no zone dependency)
  const fetchAreas = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL_TWO}usp_GetAreas`);
      setAreas(response?.data?.Data || []);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  // Fetch customer for editing
  const fetchCustomer = async (id) => {
    try {
      const response = await axiosInstance.get('', { params: { sp: 'usp_GetCustomerById', customerId: id } });
      const data = response?.data?.Data?.[0];
      setInitialData(data);
      setIsActive(data?.isActive);
      setSelectedNationality(data?.nationality);
      setSelectedArea(data?.areaId);
    } catch (error) {
      console.error('Error fetching customer:', error);
    }
  };

  useEffect(() => {
    fetchNationalities();
    fetchAreas();
    if (id) {
      fetchCustomer(id);
    }
  }, [id]);

  // Validation schema
  const validationSchema = Yup.object({
    customerNameEng: Yup.string().required('Customer Name in English is required'),
    customerNameAr: Yup.string().required('Customer Name in Arabic is required'),
    contractNumber: Yup.string().required('Contract Number is required'),
    contractId: Yup.string().required('Civil ID is required').length(12, 'Civil ID must be exactly 12 characters'),
    subscriberNumber: Yup.string().required('Subscriber Number is required'),
    contactNumber1: Yup.string().required('Contact Number 1 is required').matches(/^\d{8}$/, 'Contact Number 1 must be exactly 8 digits'),
    contactNumber2: Yup.string().matches(/^\d{8}$/, 'Contact Number 2 must be exactly 8 digits'),
    nationality: Yup.string().required('Nationality is required'),
    areaId: Yup.string().required('Area is required'),
    isActive: Yup.bool().required('Active status is required'),
  });

  return (
    <div className="page-content mt-4">
      <div className="d-flex justify-content-between align-items-center mx-4">
        <Breadcrumb title={id ? 'Edit Customer' : 'Add Customer'} breadcrumbItems={[{ title: "Dashboard", link: `/dashboard` }, { title: "Customers", link: `/customers` }]} />
        <Button className="text-white bg-primary" onClick={() => navigate(previousLocation)}>
          Back
        </Button>
      </div>

      <Formik
        enableReinitialize
        initialValues={{
          customerNameEng: initialData?.customerNameEng || '',
          customerNameAr: initialData?.customerNameAr || '',
          contractNumber: initialData?.contractNumber || '',
          contractId: initialData?.contractId || '',
          subscriberNumber: initialData?.subscriberNumber || '',
          contactNumber1: initialData?.contactNumber1 || '',
          contactNumber2: initialData?.contactNumber2 || '',
          nationality: initialData?.nationality || '',
          areaId: initialData?.areaId || '',
          isActive: initialData?.isActive == null ? true : initialData?.isActive,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          if (id) {
            dispatch(updateCustomerRequest({
              customerId: Number(id),
              sp: 'usp_UpdateCustomer',
              solutionId: 1,
              ...values
            }, navigate, resetForm));
          } else {
            dispatch(addCustomerRequest({
              sp: 'usp_InsertCustomer',
              solutionId: 1,
              ...values
            }, navigate, resetForm));
          }
        }}
      >
        {({ values, setFieldValue }) => (
          <Form className='bg-white py-3' style={{ borderRadius: "8px" }}>
            <Row className="mx-3">
              {/* Customer Name English */}
              <Col md={6}>
                <FormGroup>
                  <Label>Customer Name (English)</Label> <span className="text-danger">*</span>
                  <Field name="customerNameEng" className="form-control" placeholder="Enter customer name in English" />
                  <ErrorMessage name="customerNameEng" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Customer Name Arabic */}
              <Col md={6}>
                <FormGroup>
                  <Label>Customer Name (Arabic)</Label> <span className="text-danger">*</span>
                  <Field name="customerNameAr" className="form-control" placeholder="Enter customer name in Arabic" />
                  <ErrorMessage name="customerNameAr" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Contract Number */}
              <Col md={6}>
                <FormGroup>
                  <Label>Contract Number</Label> <span className="text-danger">*</span>
                  <Field name="contractNumber" className="form-control" placeholder="Enter contract number" />
                  <ErrorMessage name="contractNumber" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Civil ID */}
              <Col md={6}>
                <FormGroup>
                  <Label>Civil ID</Label> <span className="text-danger">*</span>
                  <Field name="contractId" className="form-control" placeholder="Enter civil ID" />
                  <ErrorMessage name="contractId" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Subscriber Number */}
              <Col md={6}>
                <FormGroup>
                  <Label>Subscriber Number</Label> <span className="text-danger">*</span>
                  <Field name="subscriberNumber" className="form-control" placeholder="Enter subscriber number" />
                  <ErrorMessage name="subscriberNumber" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Contact Number 1 */}
              <Col md={6}>
                <FormGroup>
                  <Label>Contact Number 1</Label> <span className="text-danger">*</span>
                  <Field name="contactNumber1" className="form-control" placeholder="Enter contact number 1" />
                  <ErrorMessage name="contactNumber1" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Contact Number 2 */}
              <Col md={6}>
                <FormGroup>
                  <Label>Contact Number 2</Label>
                  <Field name="contactNumber2" className="form-control" placeholder="Enter contact number 2" />
                  <ErrorMessage name="contactNumber2" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Nationality */}
              <Col md={6}>
                <FormGroup>
                  <Label>Nationality</Label> <span className="text-danger">*</span>
                  <Select
                    name="nationality"
                    options={nationalities.map(n => ({ value: n.id, label: n.name }))}
                    value={nationalities.map(n => ({ value: n.id, label: n.name })).find(n => n.value == values?.nationality)}
                    onChange={opt => {
                      setFieldValue('nationality', opt?.value || '');
                      setSelectedNationality(opt?.value);
                    }}
                    placeholder="Select Nationality"
                    isClearable
                  />
                  <ErrorMessage name="nationality" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Area */}
              <Col md={6}>
                <FormGroup>
                  <Label>Area</Label> <span className="text-danger">*</span>
                  <Select
                    name="areaId"
                    options={areas.map(a => ({ value: a.areaId, label: a.areaName }))}
                    value={areas.map(a => ({ value: a.areaId, label: a.areaName })).find(a => a.value == values?.areaId)}
                    onChange={opt => {
                      setFieldValue('areaId', opt?.value || '');
                      setSelectedArea(opt?.value);
                    }}
                    placeholder="Select Area"
                    isClearable
                  />
                  <ErrorMessage name="areaId" component="div" className="text-danger" />
                </FormGroup>
              </Col>

              {/* Is Active */}
              <Col md={6} className="d-flex align-items-center gap-2 mt-2">
                <Label className="mb-0">Is Active</Label>
                <Switch
                  uncheckedIcon={<Offsymbol />}
                  checkedIcon={<OnSymbol />}
                  onColor="#626ed4"
                  onChange={() => setFieldValue('isActive', !values.isActive)}
                  checked={values.isActive}
                  width={40}
                  height={20}
                  handleDiameter={18}
                />
              </Col>

              <Col xs={12} className="mt-4">
                <Button color="primary" type="submit">
                  {id ? 'Update' : 'Submit'}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateCustomer;
